import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBTable,
} from "mdbreact";
import { axioKit, fullName } from "../../../../../services/utilities";
import {
  formatDate,
  getCalibrationStatus,
  getNextPmDate,
  getPmLabel,
  getWarrantyStatus,
} from "../../../serviceEngineer/machines/shared/utils";

const MACHINES_URL = "procurements/operationals/machines/fixedAssets";

const CONDITION = {
  "fully functional": { label: "Fully functional", color: "success", icon: "check-circle" },
  functional: { label: "Functional", color: "info", icon: "check" },
  damaged: { label: "Damaged", color: "warning", icon: "exclamation-triangle" },
  broken: { label: "Broken", color: "danger", icon: "times-circle" },
};

const FILTERS = [
  { key: "", label: "All" },
  { key: "attention", label: "Needs attention" },
  { key: "owned", label: "Owned" },
  { key: "tieup", label: "Tie-up" },
];

const label = (value = "") =>
  String(value)
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Merchandise > Machines: the machines and equipment of the branch, owned or
 * tied-up with a supplier, with their condition, PM schedule and warranty.
 * What needs attention (broken, damaged, PM overdue, warranty ending) comes first.
 */
export default function Machines() {
  const { token, activePlatform } = useSelector(({ auth }) => auth),
    branchId = activePlatform?.branchId,
    [machines, setMachines] = useState([]),
    [isLoading, setIsLoading] = useState(false),
    [search, setSearch] = useState(""),
    [filter, setFilter] = useState(""),
    [category, setCategory] = useState(""),
    { addToast } = useToasts();

  const load = useCallback(() => {
    if (!token || !branchId) return;
    setIsLoading(true);
    axioKit
      .universal(`${MACHINES_URL}/browse`, token, { branchId })
      // only what is at this branch, not the stock it supplies to others
      .then(({ payload = [] }) =>
        setMachines(
          payload.filter(
            ({ branch }) => String(branch?._id || branch) === String(branchId),
          ),
        ),
      )
      .catch((error) =>
        addToast(error.message, { appearance: "error", autoDismiss: true }),
      )
      .finally(() => setIsLoading(false));
  }, [token, branchId, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(
    () =>
      machines
        .map((machine) => {
          const condition =
            CONDITION[String(machine.status || "").toLowerCase()] ||
            CONDITION.functional;
          const pm = getCalibrationStatus(machine);
          const warranty = getWarrantyStatus(machine.warranty);
          const attention = [
            ["damaged", "broken"].includes(String(machine.status).toLowerCase()) &&
              condition.label,
            pm.tone === "danger" && "PM overdue",
            pm.tone === "warning" && "PM due soon",
            warranty.tone === "warning" && "Warranty ending",
          ].filter(Boolean);
          return { machine, condition, pm, warranty, attention };
        })
        // what needs attention first
        .sort(
          (a, b) =>
            b.attention.length - a.attention.length ||
            String(a.machine.brand || "").localeCompare(String(b.machine.brand || "")),
        ),
    [machines],
  );

  const categories = useMemo(
    () => [...new Set(machines.map(({ category: c }) => c).filter(Boolean))].sort(),
    [machines],
  );

  const summary = useMemo(
    () => ({
      total: rows.length,
      working: rows.filter(({ machine }) =>
        ["fully functional", "functional"].includes(String(machine.status).toLowerCase()),
      ).length,
      down: rows.filter(({ machine }) =>
        ["damaged", "broken"].includes(String(machine.status).toLowerCase()),
      ).length,
      pmOverdue: rows.filter(({ pm }) => pm.tone === "danger").length,
      lis: rows.filter(({ machine }) => machine.lisCapable).length,
    }),
    [rows],
  );

  const visible = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter(({ machine, attention }) => {
      if (category && machine.category !== category) return false;
      if (filter === "attention" && !attention.length) return false;
      if (["owned", "tieup"].includes(filter) && machine.owwnersship !== filter)
        return false;
      const text = [
        machine.brand,
        machine.model,
        machine.serial,
        machine.descriptions,
        machine.section,
        machine.location,
        machine.suppllier?.name,
        machine.suppllier?.displayname,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return words.every((word) => text.includes(word));
    });
  }, [rows, search, filter, category]);

  const counts = {
    "": rows.length,
    attention: rows.filter(({ attention }) => attention.length).length,
    owned: rows.filter(({ machine }) => machine.owwnersship === "owned").length,
    tieup: rows.filter(({ machine }) => machine.owwnersship === "tieup").length,
  };

  const tiles = [
    { title: "Machines", value: summary.total, icon: "laptop-medical", color: "info" },
    { title: "Working", value: summary.working, icon: "check-circle", color: "success" },
    { title: "Damaged / broken", value: summary.down, icon: "tools", color: "danger" },
    { title: "PM overdue", value: summary.pmOverdue, icon: "calendar-times", color: "warning" },
    { title: "LIS connected", value: summary.lis, icon: "network-wired", color: "primary" },
  ];

  return (
    <MDBCard narrow className="pb-3">
      <MDBCardBody>
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div>
            <h5 className="mb-0">
              <MDBIcon icon="laptop-medical" className="mr-2 text-primary" />
              Machines
            </h5>
            <small className="text-muted">
              Machines and equipment of the branch, their condition, preventive
              maintenance (PM) and warranty.
            </small>
          </div>
          <MDBBtn size="sm" color="light" className="m-0" onClick={load} disabled={isLoading}>
            <MDBIcon icon="sync" spin={isLoading} className="mr-1" /> Refresh
          </MDBBtn>
        </div>

        {/* summary */}
        <div className="row mb-3">
          {tiles.map(({ title, value, icon, color }) => (
            <div key={title} className="col mb-2" style={{ minWidth: 150 }}>
              <div className="border rounded p-2 h-100 d-flex align-items-center">
                <MDBIcon icon={icon} className={`text-${color} mr-3`} size="lg" />
                <div>
                  <div className="h5 font-weight-bold mb-0">
                    {isLoading && !machines.length ? "…" : value}
                  </div>
                  <small className="text-muted">{title}</small>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* filters */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div className="btn-group flex-wrap" role="group" aria-label="Filter">
            {FILTERS.map(({ key, label: text }) => (
              <button
                key={key || "all"}
                type="button"
                className={`btn btn-sm m-0 z-depth-0 ${
                  filter === key ? "btn-primary" : "btn-outline-primary"
                }`}
                style={{ textTransform: "none" }}
                onClick={() => setFilter(key)}
              >
                {text}
                <MDBBadge
                  color={filter === key ? "light" : "primary"}
                  className={`ml-2 ${filter === key ? "text-dark" : ""}`}
                >
                  {counts[key]}
                </MDBBadge>
              </button>
            ))}
          </div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            {categories.length > 1 && (
              <select
                className="browser-default custom-select custom-select-sm"
                style={{ width: 170 }}
                value={category}
                onChange={({ target }) => setCategory(target.value)}
                aria-label="Category"
              >
                <option value="">All categories</option>
                {categories.map((value) => (
                  <option key={value} value={value}>
                    {label(value)}
                  </option>
                ))}
              </select>
            )}
            <div className="position-relative" style={{ width: 260 }}>
              <MDBIcon
                icon="search"
                className="position-absolute text-muted"
                style={{ top: "50%", left: 10, transform: "translateY(-50%)" }}
              />
              <input
                className="form-control form-control-sm"
                style={{ paddingLeft: 30 }}
                placeholder="Search brand, model, serial, section"
                value={search}
                onChange={({ target }) => setSearch(target.value)}
              />
            </div>
          </div>
        </div>

        <MDBTable responsive hover small className="mb-0">
          <thead className="grey lighten-4">
            <tr>
              <th>Machine</th>
              <th>Where</th>
              <th>Ownership</th>
              <th>Condition</th>
              <th>Preventive maintenance</th>
              <th>Warranty</th>
            </tr>
          </thead>
          <tbody>
            {!visible.length && (
              <tr>
                <td colSpan={6} className="text-center text-muted py-5">
                  {isLoading
                    ? "Loading machines…"
                    : !rows.length
                      ? "No machine registered for this branch yet."
                      : "No machine matches your filters."}
                </td>
              </tr>
            )}
            {visible.map(({ machine, condition, pm, warranty, attention }) => {
              const nextPm = getNextPmDate(machine);
              return (
                <tr key={machine._id} className={attention.length ? "red lighten-5" : ""}>
                  <td className="align-middle">
                    <span className="font-weight-bold">
                      {[machine.brand, machine.model].filter(Boolean).join(" ") ||
                        "Unnamed machine"}
                    </span>
                    {machine.lisCapable && (
                      <MDBBadge color="primary" className="ml-2" title="Results are sent to the LIS">
                        LIS
                      </MDBBadge>
                    )}
                    <small className="d-block text-muted">
                      {[
                        machine.category && label(machine.category),
                        machine.serial && `S/N ${machine.serial}`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </small>
                    {machine.descriptions && (
                      <small className="d-block text-muted">{machine.descriptions}</small>
                    )}
                  </td>
                  <td className="align-middle small">
                    {machine.section || <span className="text-muted">—</span>}
                    {machine.location && (
                      <span className="d-block text-muted">{machine.location}</span>
                    )}
                    {machine.assignedTo?.fullName && (
                      <span className="d-block text-muted">
                        {fullName(machine.assignedTo.fullName)}
                      </span>
                    )}
                  </td>
                  <td className="align-middle small">
                    {machine.owwnersship === "owned" ? (
                      <>
                        <span className="font-weight-bold">Owned</span>
                        <span className="d-block text-muted">
                          {label(machine.acquired || "")}
                          {machine.dop && ` · bought ${formatDate(machine.dop)}`}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="font-weight-bold">Tie-up</span>
                        <span className="d-block text-muted">
                          {machine.suppllier?.displayname ||
                            machine.suppllier?.name ||
                            "Supplier not set"}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="align-middle">
                    <MDBBadge color={condition.color}>
                      <MDBIcon icon={condition.icon} className="mr-1" />
                      {condition.label}
                    </MDBBadge>
                    {machine.conditionNotes && (
                      <small className="d-block text-muted">{machine.conditionNotes}</small>
                    )}
                  </td>
                  <td className="align-middle small">
                    <MDBBadge color={pm.tone}>{pm.label}</MDBBadge>
                    <span className="d-block text-muted">
                      {machine.lastPMDate
                        ? `Last ${formatDate(machine.lastPMDate)}`
                        : "No PM recorded"}
                      {machine.pm?.unit && ` · every ${getPmLabel(machine.pm)}`}
                    </span>
                    {nextPm && (
                      <span className={`d-block ${pm.tone === "danger" ? "text-danger" : "text-muted"}`}>
                        Next {formatDate(nextPm)}
                      </span>
                    )}
                  </td>
                  <td className="align-middle small">
                    <MDBBadge color={warranty.tone}>{warranty.label}</MDBBadge>
                    {warranty.daysLeft !== null && (
                      <span className="d-block text-muted">
                        {warranty.daysLeft < 0
                          ? `ended ${formatDate(machine.warranty)}`
                          : `until ${formatDate(machine.warranty)}`}
                      </span>
                    )}
                    {warranty.daysLeft === null && machine.warranty && (
                      <span className="d-block text-muted">{machine.warranty}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </MDBTable>
      </MDBCardBody>
    </MDBCard>
  );
}
